/**
 * Route → header eyebrow and title.
 *
 * The redesign moved the page title out of each page's own body and into the sticky header, so
 * it has to be resolvable from the route alone. The eyebrow names the sidebar group the page
 * belongs to, which is why it does not always match the page's parent route (a tour editor sits
 * under Business Units but its eyebrow reads "Tours", the screen it was opened from).
 *
 * @module config
 */

/** The two lines of the page header. */
export interface PageTitle {
  eyebrow: string;
  title: string;
}

/**
 * Exact route matches, longest-prefix wins for nested routes. Keep in sync with
 * `authenticated.routes.tsx`.
 */
const TITLE_BY_ROUTE: Record<string, PageTitle> = {
  '/dashboard': { eyebrow: 'BFF Apps', title: 'Visão geral' },
  '/business-units': { eyebrow: 'Business Units', title: 'Gerenciar BUs' },
  '/business-units/whats-new': { eyebrow: 'Business Units', title: "What's New" },
  '/business-units/whats-new/create': { eyebrow: "What's New", title: 'Criar anúncio' },
  '/business-units/pages': { eyebrow: 'Business Units', title: 'Páginas' },
  '/business-units/tours': { eyebrow: 'Business Units', title: 'Tours' },
  '/opt-in-features': { eyebrow: 'Business Units', title: 'Funcionalidades opt-in' },
  '/opt-in-features/new': { eyebrow: 'Funcionalidades opt-in', title: 'Nova funcionalidade' },
  '/surveys': { eyebrow: 'Business Units', title: 'Pesquisas' },
  '/surveys/new': { eyebrow: 'Pesquisas', title: 'Nova pesquisa' },
  '/signatures': { eyebrow: 'Operação', title: 'Assinaturas' },
  '/signatures/create': { eyebrow: 'Assinaturas', title: 'Criar assinatura' },
  '/settings/users': { eyebrow: 'Administração', title: 'Usuários' },
  '/settings/users/create': { eyebrow: 'Usuários', title: 'Novo usuário' },
};

/**
 * Routes with a dynamic segment, matched segment by segment.
 *
 * Only needed where the title must differ from the parent's. A route not listed here falls back
 * to its parent (`/settings/users/:id` simply reads "Usuários"), which is usually what you want.
 */
const TITLE_BY_PATTERN: [pattern: string, title: PageTitle][] = [
  [
    '/business-units/whats-new/:whatsNewId/edit',
    { eyebrow: "What's New", title: 'Editar anúncio' },
  ],
  ['/opt-in-features/:featureId/adoption', { eyebrow: 'Funcionalidades opt-in', title: 'Adoção' }],
  [
    '/opt-in-features/:featureId',
    { eyebrow: 'Funcionalidades opt-in', title: 'Editar funcionalidade' },
  ],
  ['/surveys/:surveyId/results', { eyebrow: 'Pesquisas', title: 'Resultados' }],
  ['/surveys/:surveyId', { eyebrow: 'Pesquisas', title: 'Editar pesquisa' }],
];

/** Matches a pathname against a pattern, treating `:param` as a single-segment wildcard. */
function matchesPattern(pathname: string, pattern: string): boolean {
  const pathSegments = pathname.split('/');
  const patternSegments = pattern.split('/');

  if (pathSegments.length !== patternSegments.length) return false;

  return patternSegments.every(
    (segment, index) => segment.startsWith(':') || segment === pathSegments[index],
  );
}

/** Used when a route has no entry, so the header never renders blank. */
const FALLBACK_TITLE: PageTitle = { eyebrow: 'BFF Apps', title: 'Administração' };

/**
 * Resolves the header for a pathname.
 *
 * Longest match wins, so `/business-units/pages` resolves to Páginas rather than the Gerenciar
 * BUs entry it is nested under. Routes with an id segment (`/settings/users/:userId`) fall back
 * to their parent's title.
 */
export function getPageTitle(pathname: string): PageTitle {
  const normalized = pathname.replace(/\/+$/, '') || '/dashboard';

  const exact = TITLE_BY_ROUTE[normalized];
  if (exact) return exact;

  const pattern = TITLE_BY_PATTERN.find(([candidate]) => matchesPattern(normalized, candidate));
  if (pattern) return pattern[1];

  const prefix = Object.keys(TITLE_BY_ROUTE)
    .filter(route => normalized.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];

  return prefix ? TITLE_BY_ROUTE[prefix] : FALLBACK_TITLE;
}
